import React, { useEffect, useMemo, useState } from 'react';
import logoSrc from '../assets/Logo_Master.svg';
import { FiChevronLeft, FiChevronRight, FiSettings } from 'react-icons/fi';
import { tourService } from '../services/tourService';
import { useAlarms } from '../hooks/useAlarms';
import { useNotifications } from '../hooks/useNotifications';
import type { AppNotification } from '../services/notificationService';
import AlarmNotificationBanner from './AlarmNotificationBanner';
import NotificationDropdown from './NotificationDropdown';
import SiteTour from './SiteTour';
import { availableTours, tourDefinitions, type TourId } from '../tours/tourDefinitions';
import { useI18n } from '../i18n';
import './DashboardShell.css';

import ActivityPage from '../pages/activity/ActivityPage';
import CommandHomePage from '../pages/activity/CommandHomePage';
import StaffPage from '../pages/guards/StaffPage';
import JoinCodesPage from '../pages/guards/JoinCodesPage';
import ReportsPage, { type ReportsCategory } from '../pages/reports/ReportsPage';
import ReportDetailPage from '../pages/reports/ReportDetailPage';
import ProfilePage from '../pages/profile/ProfilePage';
import DutyBoardPage from '../pages/schedule/DutyBoardPage';
import GuardShiftsPage from '../pages/schedule/GuardShiftsPage';
import LeaveManagementPage from '../pages/schedule/LeaveManagementPage';
import SitesPage, { type SiteBoardClientTarget } from '../pages/sites/SitesPage';
import AdminPage from '../pages/admin/AdminPage';
import RolesPage from '../pages/admin/RolesPage';
import AdminPayrollPage from '../pages/admin/AdminPayrollPage';
import PrivacyRequestsPage from '../pages/admin/PrivacyRequestsPage';
import ClientRequestsPage from '../pages/admin/ClientRequestsPage';
import PayBandsPage from '../pages/payroll/PayBandsPage';
import PayrollWorkflow from '../pages/payroll/PayrollWorkflow';
import TimesheetsPage from '../pages/payroll/TimesheetsPage';
import CreatePatrolPage, { type SiteGeofenceEditTarget } from '../pages/patrols/CreatePatrolPage';
import CreatePostsPage from '../pages/patrols/CreatePostsPage';
import StaffProfilePage from '../pages/guards/StaffProfilePage';
import AlarmsPage from '../pages/alarms/AlarmsPage';
import AnalyticsPage from '../pages/analytics/AnalyticsPage';
import ProfitabilityPage from '../pages/analytics/ProfitabilityPage';
import GuardLicencesPage from '../pages/guards/GuardLicencesPage';
import BillingPage from '../pages/billing/BillingPage';
import { useEntitlements } from '../hooks/useEntitlements';
import { billingService } from '../services/billingService';
import {
  defaultAccessForTier,
  hasPermission,
  isSystemAdministrator,
  type StaffAccess,
} from '../services/roleService';
import {
  landingFor,
  navItems,
  resolveRootIndex,
  SETTINGS_INDEX,
  resolveWorkspaceNav,
  type NavItem,
  type SubItem,
  type WorkspaceRole,
} from './workspaceNav';

export type { WorkspaceRole } from './workspaceNav';

interface DutyBoardNavigationTarget {
  siteId: string;
  date: string;
  dutyId?: string | null;
  nonce: number;
}

interface ShiftHistoryNavigationTarget {
  shiftExecutionId: string;
  nonce: number;
}

interface AlarmNavigationTarget {
  alarmId: string;
  nonce: number;
}

/** Where a report detail view was opened from, so Back can return there. */
interface ReportOrigin {
  index: number;
  subIndex: number;
  /** Untranslated source string — translated at render so it follows language changes. */
  backLabel: string;
}

const getNotificationDataString = (notification: AppNotification, key: string): string | null => {
  const value = notification.data?.[key];
  return typeof value === 'string' && value.trim() ? value : null;
};

interface DashboardShellProps {
  /** The tier the user's roles add up to. */
  userRole?: WorkspaceRole;
  /** Their roles and what those open. Omitted, the tier's own defaults apply. */
  access?: StaffAccess;
}

export default function DashboardShell({ userRole = 'admin', access: accessProp }: DashboardShellProps) {
  const { t } = useI18n();
  const access = useMemo(
    () => accessProp ?? defaultAccessForTier(userRole),
    [accessProp, userRole],
  );
  const [selectedIndex, setSelectedIndex] = useState<number>(() => landingFor(access).index);
  const [selectedSubIndex, setSelectedSubIndex] = useState<number>(() => landingFor(access).subIndex);
  const [selectedReportId, setSelectedReportId] = useState<string | null>(null);
  // Set when a report is opened from outside the Reports section, so Back returns there.
  const [reportOrigin, setReportOrigin] = useState<ReportOrigin | null>(null);
  const [selectedGuardId, setSelectedGuardId] = useState<string | null>(null);
  const [selectedGuardLicenceId, setSelectedGuardLicenceId] = useState<string | null>(null);
  const [guardProfileSource, setGuardProfileSource] = useState<number>(1);
  // Start with labels visible so first-time users do not have to decode an
  // icon-only rail. The compact rail remains one click away for dense screens.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [dutyBoardNavigationTarget, setDutyBoardNavigationTarget] = useState<DutyBoardNavigationTarget | null>(null);
  const [shiftHistoryNavigationTarget, setShiftHistoryNavigationTarget] = useState<ShiftHistoryNavigationTarget | null>(null);
  const [alarmNavigationTarget, setAlarmNavigationTarget] = useState<AlarmNavigationTarget | null>(null);
  const [siteGeofenceEditTarget, setSiteGeofenceEditTarget] = useState<(SiteGeofenceEditTarget & { nonce: number }) | null>(null);
  const [siteBoardClientTarget, setSiteBoardClientTarget] = useState<SiteBoardClientTarget | null>(null);
  const [activeTourId, setActiveTourId] = useState<TourId | null>(null);

  const {
    activeAlarms,
    newAlarms,
    dismissNewAlarm,
    acknowledgeAlarm,
    resolveAlarm,
    isLoading: alarmsLoading,
    error: alarmsError,
  } = useAlarms();
  const {
    notifications,
    unreadCount,
    markRead,
    markAllRead,
    isLoading: notificationsLoading,
    error: notificationsError,
  } = useNotifications();

  const openAlarmCount = activeAlarms.filter((a) => a.status === 'open').length;
  const currentRootIndex = resolveRootIndex(selectedIndex);
  const { has: hasEntitlement } = useEntitlements();
  const { visibleNavItems, tabsFor, canOpen } = useMemo(
    () => resolveWorkspaceNav(access, hasEntitlement),
    [access, hasEntitlement],
  );

  const activeTourSteps = useMemo(() => {
    if (!activeTourId) return [];

    // A step that points at part of the sidebar these roles do not have, or
    // at the Ops Centre home they cannot open, would highlight nothing.
    const hiddenTargets = new Set(
      navItems
        .filter((item) => !visibleNavItems.includes(item))
        .map((item) => `nav-${item.label.toLowerCase()}`),
    );
    const opsCentreOpen = canOpen(0);

    return tourDefinitions[activeTourId].steps.filter((step) => {
      if (step.roles && !step.roles.includes(userRole)) return false;
      if (!step.target) return true;
      if (hiddenTargets.has(step.target)) return false;
      return opsCentreOpen || !(step.target === 'nav-command' || step.target.startsWith('command-'));
    });
  }, [activeTourId, userRole, visibleNavItems, canOpen]);

  // Tours a user can replay from Settings: only those that start somewhere they can go.
  const replayableTours = useMemo(
    () => availableTours.filter((tour) => canOpen(resolveRootIndex(tour.destination.index))),
    [canOpen],
  );

  const scheduleTabs = tabsFor(5);
  const guardTabs = tabsFor(1);
  const activityTabs = tabsFor(0);
  const alarmsTabs = tabsFor(9);
  const reportTabs = tabsFor(2);
  const adminTabs = tabsFor(10);
  const analyticsTabs = tabsFor(4);
  const payrollTabs = tabsFor(11);

  // Metering counts an admin or manager as active for any day they used the
  // portal. Idempotent per user per day, and a failure is swallowed inside the
  // service: billing must never be why someone cannot reach their workspace.
  useEffect(() => {
    void billingService.recordSignIn();
  }, []);

  // Show the site tour automatically on the user's first visit.
  useEffect(() => {
    let cancelled = false;
    void tourService.shouldShowTour().then((show) => {
      if (!cancelled && show) {
        setSidebarCollapsed(false);
        setActiveTourId('site');
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const startTour = (tourId: TourId) => {
    const tour = tourDefinitions[tourId];
    setSelectedIndex(tour.destination.index);
    setSelectedSubIndex(tour.destination.subIndex);
    setSelectedReportId(null);
    setReportOrigin(null);
    if (tour.expandSidebar) setSidebarCollapsed(false);
    setActiveTourId(tourId);
  };

  const replayTourHere = (tourId: TourId) => {
    setActiveTourId(tourId);
  };

  const handleTourStepChange = (step: { destination?: { index: number; subIndex: number } }) => {
    if (!step.destination) return;
    setSelectedIndex(step.destination.index);
    setSelectedSubIndex(step.destination.subIndex);
    setSelectedReportId(null);
    setReportOrigin(null);
  };

  const handleTourClose = () => {
    const completedTourId = activeTourId;
    setActiveTourId(null);
    if (completedTourId === 'site') void tourService.markTourSeen();
  };

  const openReport = (reportId: string | null, origin: ReportOrigin | null = null) => {
    setSelectedReportId(reportId);
    setReportOrigin(origin);
    setSelectedIndex(2);
    setSelectedSubIndex(0);
  };

  const closeReport = () => {
    setSelectedReportId(null);
    if (reportOrigin) {
      setSelectedIndex(reportOrigin.index);
      setSelectedSubIndex(reportOrigin.subIndex);
      setReportOrigin(null);
    }
  };

  const handleNavClick = (item: NavItem) => {
    setSelectedIndex(item.index);
    setSelectedSubIndex(tabsFor(item.index)[0]?.subIndex ?? 0);
    setSelectedReportId(null);
    setReportOrigin(null);
    setSelectedGuardLicenceId(null);
  };

  const handleSubNavClick = (parentIndex: number, subIndex: number) => {
    setSelectedIndex(parentIndex);
    setSelectedSubIndex(subIndex);
    setReportOrigin(null);
    setSelectedGuardLicenceId(null);
  };

  const goToAlarms = () => {
    setSelectedIndex(9);
    setSelectedSubIndex(0);
    setSelectedReportId(null);
    setReportOrigin(null);
  };

  /** Opens the Active alarms queue with one alarm brought into view. */
  const openAlarm = (alarmId: string) => {
    setAlarmNavigationTarget({ alarmId, nonce: Date.now() });
    goToAlarms();
  };

  const openDutyBoardIssue =({ siteId, date, dutyId }: { siteId: string | null; date: string; dutyId?: string | null }) => {
    setDutyBoardNavigationTarget(siteId ? { siteId, date, dutyId, nonce: Date.now() } : null);
    setSelectedIndex(5);
    setSelectedSubIndex(7);
    setSelectedReportId(null);
    setReportOrigin(null);
    setSelectedGuardLicenceId(null);
  };

  const openLeaveRequests = () => {
    setSelectedIndex(5);
    setSelectedSubIndex(9);
    setSelectedReportId(null);
    setReportOrigin(null);
    setSelectedGuardLicenceId(null);
  };

  const openLicence = (licenceId: string) => {
    setSelectedGuardLicenceId(licenceId);
    setSelectedIndex(1);
    setSelectedSubIndex(1);
    setSelectedReportId(null);
    setReportOrigin(null);
  };

  const openReportsCategory = (category: ReportsCategory) => {
    setSelectedIndex(2);
    setSelectedSubIndex(category === 'summary' ? 1 : 0);
    setSelectedReportId(null);
    setReportOrigin(null);
  };

  const handleNotificationGoTo = (notification: AppNotification, origin?: ReportOrigin) => {
    switch (notification.type) {
      case 'licence_submitted':
      case 'licence_reviewed': {
        setSelectedGuardLicenceId(getNotificationDataString(notification, 'licence_id'));
        setSelectedIndex(1);
        setSelectedSubIndex(1);
        setSelectedReportId(null);
        setReportOrigin(null);
        break;
      }
      case 'report_assigned':
      case 'report_updated':
      case 'report_comment': {
        openReport(getNotificationDataString(notification, 'report_id'), origin);
        break;
      }
      case 'alarm_triggered':
        goToAlarms();
        break;
      case 'shift_assigned':
      case 'shift_updated':
      case 'task_updated':
        setSelectedIndex(5);
        setSelectedSubIndex(8);
        setSelectedReportId(null);
        setReportOrigin(null);
        break;
      default:
        break;
    }
  };

  /** Admin > Clients hands a client over to the Sites area, filtered to it. */
  const openSitesForClient = (clientContractId: string) => {
    setSiteBoardClientTarget({ clientContractId, nonce: Date.now() });
    setSelectedIndex(6);
    setSelectedSubIndex(0);
  };

  const openSiteGeofenceEditor = (target: SiteGeofenceEditTarget) => {
    setSiteGeofenceEditTarget({ ...target, nonce: Date.now() });
    setSelectedIndex(6);
    setSelectedSubIndex(0);
  };

  const renderNoAccess = () => (
    <div className="workspace-section">
      <div className="card workspace-no-access" role="status">
        <h2 className="workspace-no-access__title">{t('This area is not part of your role')}</h2>
        <p className="text-muted">
          {t('Your roles do not include this part of the workspace. Ask a system administrator if you need it.')}
        </p>
      </div>
    </div>
  );

  const renderSectionTabs = (tabs: SubItem[], parentIndex: number) => (
    <div className="workspace-section-tabs" data-tour={parentIndex === 11 ? 'payroll-navigation' : undefined}>
      {tabs.map((tab) => (
        <button
          key={tab.subIndex}
          type="button"
          className={`workspace-section-tab ${currentRootIndex === parentIndex && selectedSubIndex === tab.subIndex ? 'workspace-section-tab--active' : ''}`}
          aria-current={currentRootIndex === parentIndex && selectedSubIndex === tab.subIndex ? 'page' : undefined}
          onClick={() => handleSubNavClick(parentIndex, tab.subIndex)}
        >
          {t(tab.label)}
        </button>
      ))}
    </div>
  );

  const renderGuardsSection = () => {
    let content: React.ReactNode;

    switch (selectedIndex) {
      case 1:
        if (selectedSubIndex === 1) {
          content = <GuardLicencesPage initialSelectedLicenceId={selectedGuardLicenceId} />;
          break;
        }
        content = (
          <StaffPage
            access={access}
            onOpenJoinCodes={isSystemAdministrator(access) ? () => setSelectedIndex(1.5) : undefined}
            onSelectStaff={(id: string) => {
              setSelectedGuardId(id);
              setSelectedGuardLicenceId(null);
              setGuardProfileSource(1);
              setSelectedIndex(1.7);
            }}
          />
        );
        break;
      case 1.5:
        // Join codes are read and issued by System Administrators only.
        content = isSystemAdministrator(access)
          ? <JoinCodesPage onBack={() => setSelectedIndex(1)} />
          : renderNoAccess();
        break;
      case 1.7:
        content = (
          <StaffProfilePage
            staffId={selectedGuardId}
            access={access}
            onBack={() => setSelectedIndex(guardProfileSource)}
            onOpenPendingLicence={(licenceId: string) => {
              setSelectedGuardLicenceId(licenceId);
              setSelectedIndex(1);
              setSelectedSubIndex(1);
            }}
          />
        );
        break;
      default:
        content = (
          <StaffPage
            access={access}
            onOpenJoinCodes={isSystemAdministrator(access) ? () => setSelectedIndex(1.5) : undefined}
            onSelectStaff={(id: string) => {
              setSelectedGuardId(id);
              setSelectedGuardLicenceId(null);
              setGuardProfileSource(1);
              setSelectedIndex(1.7);
            }}
          />
        );
        break;
    }

    return (
      <div className="workspace-section">
        {renderSectionTabs(guardTabs, 1)}
        {content}
      </div>
    );
  };

  const renderScheduleSection = () => {
    let content: React.ReactNode;

    switch (selectedSubIndex) {
      case 8:
        content = <GuardShiftsPage />;
        break;
      case 9:
        content = <LeaveManagementPage />;
        break;
      default:
        content = (
          <DutyBoardPage
            canSeePay={hasPermission(access, 'payroll')}
            onReplayTour={() => startTour('schedule')}
            onNavigateToCreatePatrol={() => setSelectedIndex(7)}
            onNavigateToCreatePost={() => setSelectedIndex(7.5)}
            navigationTarget={dutyBoardNavigationTarget}
            onNavigationHandled={() => setDutyBoardNavigationTarget(null)}
          />
        );
        break;
    }

    return (
      <div className="workspace-section workspace-section--fill">
        {renderSectionTabs(scheduleTabs, 5)}
        {content}
      </div>
    );
  };

  const renderActivitySection = () => {
    const content = selectedSubIndex === 2 ? (
      <CommandHomePage
        activeAlarms={activeAlarms}
        alarmsReady={!alarmsLoading}
        alarmsAvailable={!alarmsError}
        notifications={notifications}
        unreadCount={unreadCount}
        notificationsReady={!notificationsLoading}
        notificationsAvailable={!notificationsError}
        onOpenAlarms={goToAlarms}
        onOpenDutyBoard={openDutyBoardIssue}
        onOpenLeave={openLeaveRequests}
        onOpenLicence={openLicence}
        onOpenReport={(reportId) => openReport(reportId, { index: 0, subIndex: 2, backLabel: 'Back to Ops Centre' })}
        onOpenReports={openReportsCategory}
        onOpenNotification={(notification) => handleNotificationGoTo(notification, {
          index: 0,
          subIndex: 2,
          backLabel: 'Back to Ops Centre',
        })}
        onMarkNotificationRead={markRead}
        onMarkAllNotificationsRead={markAllRead}
        onOpenLiveView={() => {
          setSelectedIndex(0);
          setSelectedSubIndex(0);
        }}
      />
    ) : (
      <ActivityPage
        activeAlarms={activeAlarms}
        activeTab={selectedSubIndex === 1 ? 'history' : 'live'}
        historyNavigationTarget={selectedSubIndex === 1 ? shiftHistoryNavigationTarget : null}
        onHistoryNavigationHandled={() => setShiftHistoryNavigationTarget(null)}
        onOpenAlarm={openAlarm}
      />
    );

    return (
      <div className="workspace-section workspace-section--fill">
        {renderSectionTabs(activityTabs, 0)}
        {content}
      </div>
    );
  };

  const renderAlarmsSection = () => (
    <div className="workspace-section">
      {renderSectionTabs(alarmsTabs, 9)}
      <AlarmsPage
        activeAlarms={activeAlarms}
        acknowledgeAlarm={acknowledgeAlarm}
        resolveAlarm={resolveAlarm}
        tab={selectedSubIndex === 1 ? 'history' : 'active'}
        onOpenReport={(reportId: string) =>
          openReport(reportId, { index: 9, subIndex: 1, backLabel: 'Back to Alarms' })
        }
        navigationTarget={alarmNavigationTarget}
        onNavigationHandled={() => setAlarmNavigationTarget(null)}
      />
    </div>
  );

  const renderAdminSection = () => {
    let content: React.ReactNode;

    switch (selectedSubIndex) {
      case 5:
        content = <RolesPage access={access} />;
        break;
      case 4:
        content = <BillingPage />;
        break;
      case 3:
        content = <ClientRequestsPage />;
        break;
      case 2:
        content = <PrivacyRequestsPage />;
        break;
      case 1:
        content = <AdminPage />;
        break;
      default:
        content = (
          <SitesPage
            defaultTab="clients"
            showTabNavigation={false}
            onOpenSites={openSitesForClient}
          />
        );
        break;
    }

    return (
      <div className="workspace-section">
        {renderSectionTabs(adminTabs, 10)}
        {content}
      </div>
    );
  };

  const renderAnalyticsSection = () => (
    <div className="workspace-section">
      {/* Most roles open Operations alone, and one tab is not worth a tab bar. */}
      {analyticsTabs.length > 1 && renderSectionTabs(analyticsTabs, 4)}
      {selectedSubIndex === 1 ? <ProfitabilityPage /> : <AnalyticsPage />}
    </div>
  );

  const renderPayrollSection = () => {
    let content: React.ReactNode;

    switch (selectedSubIndex) {
      case 1:
        content = <TimesheetsPage canLockPeriod={userRole === 'admin'} />;
        break;
      case 2:
        // Managers never see the tab, but guard the render too in case the
        // sub-index is carried over from another section.
        content = userRole === 'admin'
          ? <PayBandsPage />
          : <AdminPayrollPage canEditSettings={false} />;
        break;
      default:
        content = <AdminPayrollPage canEditSettings={userRole === 'admin'} />;
        break;
    }

    return (
      <div className="workspace-section">
        {renderSectionTabs(payrollTabs, 11)}
        <div className="pay-workflow-slot">
          <PayrollWorkflow
            canManageRates={userRole === 'admin'}
            currentSubIndex={selectedSubIndex}
            onNavigate={(subIndex) => handleSubNavClick(11, subIndex)}
            onReplayTour={() => startTour('payroll')}
          />
        </div>
        {content}
      </div>
    );
  };

  const renderPage = () => {
    // Sections with tabs are checked down to the tab, so a sub-index carried
    // over from another section cannot open a tab these roles do not have.
    const hasTabs = Boolean(navItems.find((item) => item.index === currentRootIndex)?.subItems);
    const tabbedOnThisView = hasTabs && selectedIndex === currentRootIndex && !selectedReportId;
    if (!canOpen(currentRootIndex, tabbedOnThisView ? selectedSubIndex : undefined)) {
      return renderNoAccess();
    }

    switch (selectedIndex) {
      case 0:
        return renderActivitySection();
      case 1:
      case 1.5:
      case 1.7:
        return renderGuardsSection();
      case 2:
        return (
          <div className="workspace-section">
            {!selectedReportId && renderSectionTabs(reportTabs, 2)}
            {selectedReportId ? (
              <ReportDetailPage
                reportId={selectedReportId}
                onBack={closeReport}
                backLabel={reportOrigin ? t(reportOrigin.backLabel) : undefined}
                onReplayShift={(shiftExecutionId: string) => {
                  setShiftHistoryNavigationTarget({
                    shiftExecutionId,
                    nonce: Date.now(),
                  });
                  setSelectedIndex(0);
                  setSelectedSubIndex(1);
                  setReportOrigin(null);
                }}
              />
            ) : (
              <ReportsPage
                category={(selectedSubIndex === 1 ? 'summary' : 'incident') as ReportsCategory}
                onOpenReport={(id: string) => {
                  // Already in the Reports section — keep the current tab so Back returns to it.
                  setSelectedReportId(id);
                  setReportOrigin(null);
                }}
              />
            )}
          </div>
        );
      case 4:
        return renderAnalyticsSection();
      case 5:
        return renderScheduleSection();
      case 6:
        return (
          <SitesPage
            geofenceEditTarget={siteGeofenceEditTarget}
            clientFilterTarget={siteBoardClientTarget}
            defaultTab="sites"
            showClientManagement={false}
            onManageClients={canOpen(10, 0) ? () => handleSubNavClick(10, 0) : undefined}
            onReplayPatrolTour={() => replayTourHere('patrol')}
            onReplayPostTour={() => replayTourHere('post')}
          />
        );
      case 7:
        return (
          <CreatePatrolPage
            onEditSiteGeofence={openSiteGeofenceEditor}
            onExit={() => setSelectedIndex(6)}
            onReplayTour={() => replayTourHere('patrol')}
          />
        );
      case 7.5:
        return (
          <CreatePostsPage
            onExit={() => setSelectedIndex(6)}
            onReplayTour={() => replayTourHere('post')}
          />
        );
      case 8:
        return <ProfilePage tours={replayableTours} onReplayTour={startTour} />;
      case 9:
        return renderAlarmsSection();
      case 10:
        return renderAdminSection();
      case 11:
        return renderPayrollSection();
      default:
        return renderActivitySection();
    }
  };

  return (
    <div className="dashboard">
      <AlarmNotificationBanner
        newAlarms={newAlarms}
        activeAlarms={activeAlarms}
        onDismiss={dismissNewAlarm}
        onAcknowledge={acknowledgeAlarm}
        onGoToAlarms={goToAlarms}
      />

      <aside className={`sidebar ${sidebarCollapsed ? 'sidebar--collapsed' : ''}`}>
        <div className="sidebar__brand">
          <div className="sidebar__brand-lockup">
            <div className="sidebar__brand-mark">
              <img src={logoSrc} alt={t('Guardian logo')} className="sidebar__brand-logo" />
            </div>
            <div className="sidebar__brand-copy">
              <div className="sidebar__logo">OdinsEye</div>
              <div className="sidebar__app-name">{t('Admin Portal')}</div>
              <div className="sidebar__brand-subtitle">{t('Security operations workspace')}</div>
            </div>
          </div>
          <button
            type="button"
            className="sidebar__toggle"
            onClick={() => setSidebarCollapsed((collapsed) => !collapsed)}
            title={sidebarCollapsed ? t('Expand sidebar') : t('Collapse sidebar')}
            aria-label={sidebarCollapsed ? t('Expand sidebar') : t('Collapse sidebar')}
            aria-expanded={!sidebarCollapsed}
            aria-controls="workspace-navigation"
          >
            {sidebarCollapsed
              ? <FiChevronRight aria-hidden="true" />
              : <FiChevronLeft aria-hidden="true" />}
          </button>
        </div>

        <nav id="workspace-navigation" className="sidebar__nav" aria-label={t('Workspace')}>
          <div className="sidebar__section-label">{t('Workspace')}</div>
          {visibleNavItems.map((item) => {
            const isActive = currentRootIndex === item.index;
            const isAlarmsItem = item.index === 9;
            const Icon = item.icon;

            return (
              <div key={item.index}>
                <button
                  type="button"
                  onClick={() => handleNavClick(item)}
                  className={`nav-item ${isActive ? 'nav-item--active' : ''} ${isAlarmsItem && openAlarmCount > 0 ? 'nav-item--alarm' : ''}`}
                  title={`${t(item.label)}: ${t(item.summary)}`}
                  aria-current={isActive ? 'page' : undefined}
                  data-tour={`nav-${item.label.toLowerCase()}`}
                >
                  <span className="nav-item__icon">
                    <Icon aria-hidden="true" />
                    {isAlarmsItem && openAlarmCount > 0 && (
                      <span className="nav-alarm-indicator" aria-hidden="true">
                        {openAlarmCount > 99 ? '99+' : openAlarmCount}
                      </span>
                    )}
                  </span>
                  <span className="nav-item__content">
                    <span className="nav-item__label-row">
                      <span className="nav-item__label">{t(item.label)}</span>
                      {isAlarmsItem && openAlarmCount > 0 && (
                        <span className="nav-alarm-badge">{openAlarmCount}</span>
                      )}
                    </span>
                    <span className="nav-item__meta">{t(item.summary)}</span>
                  </span>
                </button>
              </div>
            );
          })}
        </nav>

        <div className="sidebar__footer">
          <button
            type="button"
            onClick={() => {
              setSelectedIndex(SETTINGS_INDEX);
              setSelectedSubIndex(0);
              setSelectedReportId(null);
            }}
            className={`nav-item ${currentRootIndex === SETTINGS_INDEX ? 'nav-item--active' : ''}`}
            title={t('Settings')}
            aria-current={currentRootIndex === SETTINGS_INDEX ? 'page' : undefined}
            data-tour="nav-settings"
          >
            <span className="nav-item__icon"><FiSettings aria-hidden="true" /></span>
            <span className="nav-item__content">
              <span className="nav-item__label-row">
                <span className="nav-item__label">{t('Settings')}</span>
              </span>
              <span className="nav-item__meta">{t('Account, language and session controls')}</span>
            </span>
          </button>
        </div>
      </aside>

      <main className="main-content">
        <div className="workspace-bar">
          <div data-tour="notifications">
            <NotificationDropdown
              notifications={notifications}
              unreadCount={unreadCount}
              onMarkAllRead={markAllRead}
              onMarkRead={markRead}
              onGoTo={handleNotificationGoTo}
            />
          </div>
        </div>

        <div className="main-content__body">
          {renderPage()}
        </div>
      </main>

      {activeTourId && (
        <SiteTour
          key={activeTourId}
          steps={activeTourSteps}
          onClose={handleTourClose}
          onStepChange={handleTourStepChange}
        />
      )}
    </div>
  );
}
